/**
 * DaVinci API ExternalAttachments Extractor
 *
 * Extracts statutory document URLs from `Booking.ExternalAttachments`
 * in DaVinci BA (Booking Action) response.
 *
 * Known attachment Types:
 * - "VVI"  - Vorvertragliche Informationen und Einreisebestimmungen
 *
 * Note: The FORM (Formblatt zur Pauschalreiserichtlinie) link is a static link
 * in the `ibe.terms.statutory.form` translation, gated by the presence of a BA
 * service line with Requirement === 'PRK' (see `bookingResponseHasPackageRequirement`).
 */

export interface ExtractedAttachments {
  /** First "VVI" attachment URL (vorvertragliche Informationen), or null if absent. */
  vviUrl: string | null;
}

/**
 * Find the first attachment matching `type` and return its `Url`.
 *
 * @param attachments - Raw ExternalAttachments array from a DaVinci Booking response
 * @param type - Attachment Type to match (e.g. "FORM", "VVI")
 * @returns URL string if found, otherwise null
 */
function findFirstAttachmentUrlByType(
  attachments: Array<Record<string, unknown>>,
  type: string
): string | null {
  const match = attachments.find(
    (a) => a.Type === type && typeof a.Url === 'string' && a.Url.trim() !== ''
  );
  return match ? (match.Url as string) : null;
}

/**
 * Extract the VVI attachment URL from a DaVinci BA response.
 *
 * Defensive: returns `{ vviUrl: null }` when ExternalAttachments
 * is missing or not an array. Callers should treat null as "field absent in this
 * response stage" rather than "explicitly cleared".
 *
 * @param bookingResponse - The Booking object from DaVinci BA response
 * @returns Extracted VVI attachment URL (null when absent)
 */
export function extractAttachmentsFromBookingResponse(
  bookingResponse: Record<string, unknown>
): ExtractedAttachments {
  const attachments = bookingResponse.ExternalAttachments as
    | Array<Record<string, unknown>>
    | undefined;

  if (!Array.isArray(attachments)) {
    return { vviUrl: null };
  }

  return {
    vviUrl: findFirstAttachmentUrlByType(attachments, 'VVI'),
  };
}
